<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import aiReviewsAPI from 'dashboard/api/aiReviews';
import Button from 'dashboard/components-next/button/Button.vue';
import PageHeader from '../components/PageHeader.vue';
import StatusBadge from '../components/StatusBadge.vue';
import { formatDateTime, getApiErrorMessage } from '../utils';

const { t, te } = useI18n();
const route = useRoute();

const review = ref(null);
const isLoading = ref(false);
const isRetrying = ref(false);

const result = computed(() => review.value?.result || {});
const criteria = computed(() =>
  Array.isArray(result.value.criteria) ? result.value.criteria : []
);
const issues = computed(() =>
  Array.isArray(result.value.issues) ? result.value.issues : []
);
const limitations = computed(() =>
  Array.isArray(review.value?.evaluation_limitations)
    ? review.value.evaluation_limitations
    : []
);
const knowledgeUsed = computed(() =>
  Array.isArray(review.value?.knowledge_used) ? review.value.knowledge_used : []
);
const messagesById = computed(() =>
  Object.fromEntries(
    (review.value?.messages || []).map(message => [message.id, message])
  )
);
const errorDetails = computed(() => review.value?.last_error_details || {});
const errorItems = computed(() =>
  Array.isArray(errorDetails.value.details) ? errorDetails.value.details : []
);
const coverage = computed(() => ({
  passed: criteria.value.filter(
    criterion =>
      criterion.applicability === 'applicable' &&
      criterion.score === criterion.max_score
  ).length,
  notApplicable: criteria.value.filter(
    criterion => criterion.applicability === 'not_applicable'
  ).length,
  notAssessable: criteria.value.filter(
    criterion => criterion.applicability === 'not_assessable'
  ).length,
  partiallyAssessed: criteria.value.filter(
    criterion => criterion.applicability === 'partially_assessed'
  ).length,
}));

const loadReview = async () => {
  try {
    isLoading.value = true;
    const { data } = await aiReviewsAPI.getReview(route.params.reviewId);
    review.value = data;
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    isLoading.value = false;
  }
};

const retryReview = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('AI_REVIEWS.REVIEW.RETRY_CONFIRM'))) return;

  try {
    isRetrying.value = true;
    const { data } = await aiReviewsAPI.retryReview(review.value.id);
    review.value = data;
    useAlert(t('AI_REVIEWS.REVIEW.RETRY_SUCCESS'));
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    isRetrying.value = false;
  }
};

const valueOrFallback = value => value ?? t('AI_REVIEWS.COMMON.NOT_AVAILABLE');
const issueAnchor = issueId => 'issue-' + issueId;
const criterionName = key =>
  criteria.value.find(criterion => criterion.key === key)?.name || key;
const applicabilityLabel = applicability =>
  t('AI_REVIEWS.REVIEW.APPLICABILITY.' + applicability.toUpperCase());
const criterionIconClass = criterion => {
  if (criterion.issue_ids.length) {
    return 'i-lucide-circle-alert text-n-ruby-10';
  }
  if (criterion.applicability === 'applicable') {
    return 'i-lucide-circle-check text-n-teal-10';
  }
  if (criterion.applicability === 'partially_assessed') {
    return 'i-lucide-circle-help text-n-amber-10';
  }
  return 'i-lucide-circle-minus text-n-slate-10';
};
const formatDuration = totalSeconds => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes
    ? t('AI_REVIEWS.REVIEW.DURATION_MINUTES_SECONDS', { minutes, seconds })
    : t('AI_REVIEWS.REVIEW.DURATION_SECONDS', { seconds });
};
const evidenceRole = messageId => {
  const message = messagesById.value[messageId];
  if (message?.role === 'agent') {
    return message.agent_id
      ? t('AI_REVIEWS.REVIEW.AGENT_EVIDENCE_WITH_ID', {
          id: message.agent_id,
        })
      : t('AI_REVIEWS.REVIEW.AGENT_EVIDENCE');
  }
  return t('AI_REVIEWS.REVIEW.COURIER_EVIDENCE');
};
const errorReasonLabel = reason => {
  const key = 'AI_REVIEWS.REVIEW.ERROR_REASONS.' + reason.toUpperCase();
  return te(key) ? t(key) : reason;
};
// Service paths are zero-based: issues.6.evidence.1, issues.6, scores.<criterion key>.
const errorLocation = path => {
  const [section, first, nested, second] = path.split('.');
  if (section === 'issues' && nested === 'evidence' && second) {
    return t('AI_REVIEWS.REVIEW.ERROR_LOCATION.EVIDENCE', {
      issue: Number(first) + 1,
      evidence: Number(second) + 1,
    });
  }
  if (section === 'issues' && first && !nested) {
    return t('AI_REVIEWS.REVIEW.ERROR_LOCATION.ISSUE', {
      issue: Number(first) + 1,
    });
  }
  if (section === 'scores' && first && !nested) {
    return t('AI_REVIEWS.REVIEW.ERROR_LOCATION.CRITERION', {
      name: criterionName(first),
    });
  }
  return path;
};
const messageRoute = messageId => ({
  name: 'inbox_conversation',
  params: {
    accountId: route.params.accountId,
    conversation_id: review.value.live_conversation.display_id,
  },
  query: { messageId },
});

onMounted(loadReview);
</script>

<template>
  <div v-if="isLoading && !review" class="p-10 text-center text-n-slate-11">
    {{ t('AI_REVIEWS.COMMON.LOADING') }}
  </div>
  <template v-else-if="review">
    <PageHeader
      :title="t('AI_REVIEWS.REVIEW.TITLE', { id: review.id })"
      :description="t('AI_REVIEWS.REVIEW.DESCRIPTION')"
    >
      <template #actions>
        <StatusBadge :status="review.status" />
        <Button
          v-if="review.status === 'failed'"
          :label="t('AI_REVIEWS.REVIEW.RETRY')"
          icon="i-lucide-rotate-ccw"
          :is-loading="isRetrying"
          @click="retryReview"
        />
      </template>
    </PageHeader>

    <RouterLink
      class="inline-flex items-center gap-1 mb-5 text-sm font-medium text-n-brand hover:underline"
      :to="{
        name: 'ai_review_batch_show',
        params: {
          accountId: route.params.accountId,
          batchId: route.params.batchId,
        },
      }"
    >
      <span class="i-lucide-arrow-left size-4" />
      {{ t('AI_REVIEWS.REVIEW.BACK_TO_BATCH', { id: route.params.batchId }) }}
    </RouterLink>

    <section class="p-5 mb-6 border rounded-xl border-n-weak bg-n-solid-2">
      <h3 class="mb-4 text-base font-semibold text-n-slate-12">
        {{ t('AI_REVIEWS.REVIEW.METADATA') }}
      </h3>
      <dl class="grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <dt class="text-n-slate-11">
            {{ t('AI_REVIEWS.REVIEW.CONVERSATION') }}
          </dt>
          <dd class="mt-1 font-medium text-n-slate-12">
            <RouterLink
              v-if="review.live_conversation.exists"
              class="text-n-brand hover:underline"
              :to="{
                name: 'inbox_conversation',
                params: {
                  accountId: route.params.accountId,
                  conversation_id: review.live_conversation.display_id,
                },
              }"
            >
              {{
                t('AI_REVIEWS.REVIEW.OPEN_CONVERSATION', {
                  id: review.live_conversation.display_id,
                })
              }}
            </RouterLink>
            <span v-else>
              {{ t('AI_REVIEWS.REVIEW.LIVE_CONVERSATION_MISSING') }}
            </span>
          </dd>
        </div>
        <div>
          <dt class="text-n-slate-11">{{ t('AI_REVIEWS.REVIEW.ASSIGNEE') }}</dt>
          <dd class="mt-1 font-medium text-n-slate-12">
            {{ valueOrFallback(review.assignment_context.assignee_name) }}
          </dd>
        </div>
        <div>
          <dt class="text-n-slate-11">{{ t('AI_REVIEWS.REVIEW.TEAM') }}</dt>
          <dd class="mt-1 font-medium text-n-slate-12">
            {{ valueOrFallback(review.assignment_context.team_name) }}
          </dd>
        </div>
        <div>
          <dt class="text-n-slate-11">{{ t('AI_REVIEWS.REVIEW.CITY') }}</dt>
          <dd class="mt-1 font-medium text-n-slate-12">
            {{ valueOrFallback(review.contact_context.city) }}
          </dd>
        </div>
        <div>
          <dt class="text-n-slate-11">{{ t('AI_REVIEWS.REVIEW.LANGUAGE') }}</dt>
          <dd class="mt-1 font-medium text-n-slate-12">
            {{ valueOrFallback(review.contact_context.language) }}
          </dd>
        </div>
        <div>
          <dt class="text-n-slate-11">{{ t('AI_REVIEWS.REVIEW.POLICY') }}</dt>
          <dd class="mt-1 font-medium text-n-slate-12">
            {{ valueOrFallback(review.policy.policy_name) }} ·
            {{
              t('AI_REVIEWS.COMMON.VERSION', { version: review.policy.version })
            }}
          </dd>
        </div>
        <div>
          <dt class="text-n-slate-11">
            {{ t('AI_REVIEWS.REVIEW.KNOWLEDGE_VERSIONS') }}
          </dt>
          <dd class="mt-1 font-medium text-n-slate-12">
            <span
              v-if="review.knowledge_base_versions.length"
              class="flex flex-col gap-1"
            >
              <span
                v-for="version in review.knowledge_base_versions"
                :key="version.id"
              >
                {{ version.name }} ·
                {{
                  t('AI_REVIEWS.COMMON.VERSION', {
                    version: version.version_number,
                  })
                }}
              </span>
            </span>
            <span v-else>{{ t('AI_REVIEWS.COMMON.NOT_AVAILABLE') }}</span>
          </dd>
        </div>
        <div>
          <dt class="text-n-slate-11">{{ t('AI_REVIEWS.REVIEW.MODEL') }}</dt>
          <dd class="mt-1 font-medium text-n-slate-12">
            {{ valueOrFallback(review.model.profile_name) }} ·
            {{ valueOrFallback(review.model.identifier) }}
          </dd>
        </div>
        <div>
          <dt class="text-n-slate-11">{{ t('AI_REVIEWS.REVIEW.PERIOD') }}</dt>
          <dd class="mt-1 font-medium text-n-slate-12">
            {{ formatDateTime(review.review_period_start) }} –
            {{ formatDateTime(review.review_period_end) }}
          </dd>
        </div>
        <div>
          <dt class="text-n-slate-11">{{ t('AI_REVIEWS.REVIEW.BOUNDARY') }}</dt>
          <dd class="mt-1 font-medium text-n-slate-12">
            #{{ review.reviewed_through_message_id }}
          </dd>
        </div>
        <div>
          <dt class="text-n-slate-11">{{ t('AI_REVIEWS.REVIEW.ATTEMPTS') }}</dt>
          <dd class="mt-1 font-medium text-n-slate-12">
            {{ review.attempt_count }}
          </dd>
        </div>
        <div>
          <dt class="text-n-slate-11">
            {{ t('AI_REVIEWS.REVIEW.LAST_ATTEMPT') }}
          </dt>
          <dd class="mt-1 font-medium text-n-slate-12">
            {{ formatDateTime(review.last_attempted_at) }}
          </dd>
        </div>
      </dl>

      <div
        v-if="review.last_error_message"
        class="p-4 mt-5 rounded-lg bg-n-ruby-3 text-n-ruby-11"
      >
        <h4 class="mb-1 text-sm font-semibold">
          {{ t('AI_REVIEWS.REVIEW.LAST_ERROR') }}
        </h4>
        <p class="mb-0 text-sm">
          {{
            errorDetails.reason
              ? errorReasonLabel(errorDetails.reason)
              : review.last_error_message
          }}
        </p>
        <ul v-if="errorItems.length" class="grid gap-2 mt-3 mb-0">
          <li
            v-for="(item, index) in errorItems"
            :key="index"
            class="p-3 text-sm rounded-lg bg-n-solid-2"
          >
            <div
              class="flex flex-wrap items-center justify-between gap-2 text-xs text-n-slate-11"
            >
              <span>
                {{ errorLocation(item.path) }}
                <template v-if="item.type !== errorDetails.reason">
                  · {{ errorReasonLabel(item.type) }}
                </template>
              </span>
              <RouterLink
                v-if="item.message_id && review.live_conversation.exists"
                class="font-medium text-n-brand hover:underline"
                :to="messageRoute(item.message_id)"
              >
                {{
                  t('AI_REVIEWS.REVIEW.OPEN_MESSAGE', { id: item.message_id })
                }}
              </RouterLink>
            </div>
            <template v-if="item.snippet">
              <p class="mt-2 mb-0 text-xs text-n-slate-11">
                {{ t('AI_REVIEWS.REVIEW.ERROR_MODEL_QUOTE') }}
              </p>
              <blockquote class="mb-0 text-n-slate-12">
                {{
                  t('AI_REVIEWS.REVIEW.EVIDENCE_QUOTE', {
                    snippet: item.snippet,
                  })
                }}
              </blockquote>
            </template>
            <template v-if="messagesById[item.message_id]">
              <p class="mt-2 mb-0 text-xs text-n-slate-11">
                {{ t('AI_REVIEWS.REVIEW.ERROR_MESSAGE_TEXT') }}
              </p>
              <blockquote class="mb-0 whitespace-pre-line text-n-slate-12">
                {{ messagesById[item.message_id].content }}
              </blockquote>
            </template>
          </li>
        </ul>
      </div>
    </section>

    <section class="p-5 mb-6 border rounded-xl border-n-weak bg-n-solid-2">
      <h3 class="mb-4 text-base font-semibold text-n-slate-12">
        {{ t('AI_REVIEWS.REVIEW.EVALUATION') }}
      </h3>
      <p
        v-if="review.status !== 'completed'"
        class="mb-0 text-sm text-n-slate-11"
      >
        {{ t('AI_REVIEWS.REVIEW.PENDING') }}
      </p>
      <template v-else>
        <dl class="grid gap-3 mb-6 sm:grid-cols-2 xl:grid-cols-5">
          <div class="p-4 text-center rounded-xl bg-n-brand/10">
            <dt class="text-sm text-n-slate-11">
              {{ t('AI_REVIEWS.REVIEW.OVERALL_SCORE') }}
            </dt>
            <dd class="mt-1 text-3xl font-semibold text-n-brand">
              {{ valueOrFallback(result.overall_score) }}
            </dd>
          </div>
          <div class="p-4 text-center border rounded-xl border-n-weak">
            <dt class="text-sm text-n-slate-11">
              {{ t('AI_REVIEWS.REVIEW.CRITERIA') }}
            </dt>
            <dd class="mt-1 text-2xl font-semibold text-n-slate-12">
              {{ criteria.length }}
            </dd>
          </div>
          <div class="p-4 text-center border rounded-xl border-n-weak">
            <dt class="text-sm text-n-slate-11">
              {{ t('AI_REVIEWS.REVIEW.PASSED') }}
            </dt>
            <dd class="mt-1 text-2xl font-semibold text-n-teal-11">
              {{ coverage.passed }}
            </dd>
          </div>
          <div class="p-4 text-center border rounded-xl border-n-weak">
            <dt class="text-sm text-n-slate-11">
              {{ t('AI_REVIEWS.REVIEW.ISSUES') }}
            </dt>
            <dd class="mt-1 text-2xl font-semibold text-n-ruby-11">
              {{ issues.length }}
            </dd>
          </div>
          <div class="p-4 text-center border rounded-xl border-n-weak">
            <dt class="text-sm text-n-slate-11">
              {{ t('AI_REVIEWS.REVIEW.COVERAGE_LIMITS') }}
            </dt>
            <dd class="mt-1 text-xs font-semibold text-n-slate-12">
              {{
                t('AI_REVIEWS.REVIEW.COVERAGE_LIMITS_VALUE', {
                  notApplicable: coverage.notApplicable,
                  notAssessable: coverage.notAssessable,
                  partial: coverage.partiallyAssessed,
                })
              }}
            </dd>
          </div>
        </dl>

        <h4 class="mb-3 text-sm font-semibold text-n-slate-12">
          {{ t('AI_REVIEWS.REVIEW.DETECTED_ISSUES') }}
        </h4>
        <p v-if="!issues.length" class="mb-0 text-sm text-n-teal-11">
          {{ t('AI_REVIEWS.REVIEW.NO_ISSUES') }}
        </p>
        <div v-else class="grid gap-4">
          <article
            v-for="issue in issues"
            :id="issueAnchor(issue.id)"
            :key="issue.id"
            class="p-4 border rounded-xl border-n-ruby-6 bg-n-ruby-3"
          >
            <div class="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <p class="mb-1 text-xs font-semibold uppercase text-n-ruby-10">
                  {{
                    t('AI_REVIEWS.REVIEW.ISSUE_HEADING', {
                      id: issue.id,
                      severity: issue.severity,
                    })
                  }}
                </p>
                <h5 class="mb-0 text-base font-semibold text-n-ruby-12">
                  {{ issue.type_name }}
                </h5>
              </div>
              <span class="text-xs text-n-ruby-11">
                {{ issue.criterion_keys.map(criterionName).join(', ') }}
              </span>
            </div>

            <p class="mb-2 text-sm text-n-ruby-12">{{ issue.finding }}</p>
            <p class="mb-3 text-sm text-n-ruby-11">
              <span class="font-semibold">
                {{ t('AI_REVIEWS.REVIEW.CORRECTION') }}
              </span>
              {{ issue.correction }}
            </p>

            <p
              v-if="issue.attributed_agent_ids.length"
              class="mb-3 text-xs text-n-ruby-11"
            >
              {{
                t('AI_REVIEWS.REVIEW.ATTRIBUTED_AGENTS', {
                  ids: issue.attributed_agent_ids.join(', '),
                })
              }}
            </p>
            <p v-else class="mb-3 text-xs text-n-ruby-11">
              {{ t('AI_REVIEWS.REVIEW.UNATTRIBUTED_ISSUE') }}
            </p>

            <p
              v-if="issue.timing"
              class="p-3 mb-3 text-sm rounded-lg bg-n-solid-2 text-n-slate-12"
            >
              {{
                t('AI_REVIEWS.REVIEW.TIMING_EVIDENCE', {
                  delay: formatDuration(issue.timing.delay_seconds),
                  allowed: formatDuration(issue.timing.allowed_seconds),
                })
              }}
            </p>

            <ul class="grid gap-2 mb-0">
              <li
                v-for="evidence in issue.evidence"
                :key="evidence.message_id"
                class="p-3 rounded-lg bg-n-solid-2"
              >
                <div
                  class="flex flex-wrap items-center justify-between gap-2 mb-1 text-xs text-n-slate-11"
                >
                  <span>{{ evidenceRole(evidence.message_id) }}</span>
                  <RouterLink
                    v-if="review.live_conversation.exists"
                    class="font-medium text-n-brand hover:underline"
                    :to="messageRoute(evidence.message_id)"
                  >
                    {{
                      t('AI_REVIEWS.REVIEW.OPEN_MESSAGE', {
                        id: evidence.message_id,
                      })
                    }}
                  </RouterLink>
                  <span v-else>
                    {{
                      t('AI_REVIEWS.REVIEW.MESSAGE_REFERENCE', {
                        id: evidence.message_id,
                      })
                    }}
                  </span>
                </div>
                <blockquote class="mb-0 text-sm text-n-slate-12">
                  {{
                    t('AI_REVIEWS.REVIEW.EVIDENCE_QUOTE', {
                      snippet: evidence.snippet,
                    })
                  }}
                </blockquote>
              </li>
            </ul>
          </article>
        </div>

        <h4 class="mt-6 mb-3 text-sm font-semibold text-n-slate-12">
          {{ t('AI_REVIEWS.REVIEW.CRITERIA') }}
        </h4>
        <p v-if="!criteria.length" class="text-sm text-n-slate-11">
          {{ t('AI_REVIEWS.REVIEW.NO_CRITERIA') }}
        </p>
        <div v-else class="overflow-hidden border rounded-xl border-n-weak">
          <article
            v-for="criterion in criteria"
            :key="criterion.key"
            class="flex flex-wrap items-center justify-between gap-3 p-3 border-b last:border-b-0 border-n-weak"
          >
            <div class="flex items-center min-w-0 gap-3">
              <span
                class="shrink-0 size-5"
                :class="criterionIconClass(criterion)"
              />
              <div>
                <h5 class="mb-0 text-sm font-medium text-n-slate-12">
                  {{ criterion.name }}
                </h5>
                <p class="mb-0 text-xs text-n-slate-11">
                  {{ applicabilityLabel(criterion.applicability) }}
                </p>
              </div>
            </div>
            <div class="flex flex-wrap items-center justify-end gap-2">
              <a
                v-for="issueId in criterion.issue_ids"
                :key="issueId"
                class="text-xs font-medium text-n-brand hover:underline"
                :href="'#' + issueAnchor(issueId)"
              >
                {{ t('AI_REVIEWS.REVIEW.VIEW_ISSUE', { id: issueId }) }}
              </a>
              <span class="text-sm font-semibold tabular-nums text-n-slate-12">
                {{
                  criterion.score === null
                    ? t('AI_REVIEWS.REVIEW.NOT_SCORED')
                    : t('AI_REVIEWS.REVIEW.SCORE_VALUE', {
                        score: criterion.score,
                        max: criterion.max_score,
                      })
                }}
              </span>
            </div>
          </article>
        </div>
      </template>
    </section>

    <section class="p-5 mb-6 border rounded-xl border-n-ruby-6 bg-n-ruby-3">
      <h3 class="mb-3 text-base font-semibold text-n-ruby-11">
        {{ t('AI_REVIEWS.REVIEW.EVIDENCE_LIMITATIONS') }}
      </h3>
      <p v-if="!limitations.length" class="mb-0 text-sm text-n-ruby-11">
        {{ t('AI_REVIEWS.REVIEW.NO_LIMITATIONS') }}
      </p>
      <ul v-else class="grid gap-3 mb-0">
        <li
          v-for="(limitation, index) in limitations"
          :key="
            [limitation.message_id, limitation.attachment_id, index].join('-')
          "
          class="p-3 text-sm rounded-lg bg-n-solid-2 text-n-slate-12"
        >
          <p class="mb-1 font-semibold">
            {{
              t('AI_REVIEWS.REVIEW.ATTACHMENT_LIMITATION', {
                messageId: limitation.message_id,
              })
            }}
          </p>
          <p class="mb-0 text-n-slate-11">{{ limitation.reason }}</p>
        </li>
      </ul>
    </section>

    <section class="p-5 border rounded-xl border-n-weak bg-n-solid-2">
      <h3 class="mb-1 text-base font-semibold text-n-slate-12">
        {{ t('AI_REVIEWS.REVIEW.KNOWLEDGE_USED') }}
      </h3>
      <p class="mb-4 text-sm text-n-slate-11">
        {{ t('AI_REVIEWS.REVIEW.KNOWLEDGE_DESCRIPTION') }}
      </p>
      <p v-if="!knowledgeUsed.length" class="mb-0 text-sm text-n-slate-11">
        {{ t('AI_REVIEWS.REVIEW.NO_KNOWLEDGE') }}
      </p>
      <div v-else class="grid gap-3">
        <details
          v-for="knowledge in knowledgeUsed"
          :key="knowledge.version_id"
          class="p-4 border rounded-lg border-n-weak bg-n-alpha-1"
        >
          <summary class="text-sm font-semibold cursor-pointer text-n-slate-12">
            {{
              knowledge.city
                ? t('AI_REVIEWS.REVIEW.CITY_KNOWLEDGE', {
                    city: knowledge.city,
                    version: knowledge.version,
                  })
                : t('AI_REVIEWS.REVIEW.GLOBAL_KNOWLEDGE', {
                    version: knowledge.version,
                  })
            }}
          </summary>
          <div class="grid gap-3 mt-4">
            <article
              v-for="entry in knowledge.entries || []"
              :key="entry.id"
              class="p-3 rounded-lg bg-n-solid-2"
            >
              <h4 class="mb-2 text-sm font-semibold text-n-slate-12">
                {{ entry.title }}
              </h4>
              <p class="mb-0 text-sm whitespace-pre-wrap text-n-slate-11">
                {{ entry.content }}
              </p>
            </article>
          </div>
        </details>
      </div>
    </section>
  </template>
</template>
